// src/index.ts
var SDK_VERSION = true ? "3.0.0" : "unknown";
var JOB_FRAME_TYPES = /* @__PURE__ */ new Set([
  "event",
  "status",
  "plan",
  "wake",
  "state",
  "question",
  "approval"
]);
var AimdocServicesError = class extends Error {
  constructor(code, message, options = {}) {
    var _a;
    super(message);
    this.name = "AimdocServicesError";
    this.code = code;
    this.status = (_a = options.status) != null ? _a : 0;
    this.retryAfter = options.retryAfter;
  }
};
var DEFAULT_SERVICES_API_URL = "https://api.aimdoc.ai";
var DEFAULT_SERVICES_TIMEOUT_MS = 1e4;
var SESSION_EXPIRY_HEADROOM_MS = 15e3;
var mutationId = () => {
  var _a;
  if (typeof ((_a = globalThis.crypto) == null ? void 0 : _a.randomUUID) === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
var waitFor = (delayMs, signal) => new Promise((resolve) => {
  if (signal.aborted) {
    resolve(false);
    return;
  }
  const timeout = setTimeout(() => {
    signal.removeEventListener("abort", onAbort);
    resolve(true);
  }, Math.max(0, delayMs));
  const onAbort = () => {
    clearTimeout(timeout);
    resolve(false);
  };
  signal.addEventListener("abort", onAbort, { once: true });
});
var AimdocSseParser = class {
  constructor() {
    this.buffer = "";
  }
  push(chunk) {
    this.buffer += chunk.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const parsed = [];
    let boundary = this.buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const block = this.buffer.slice(0, boundary);
      this.buffer = this.buffer.slice(boundary + 2);
      let id = null;
      let event = "message";
      const data = [];
      for (const line of block.split("\n")) {
        if (!line || line.startsWith(":")) continue;
        const colon = line.indexOf(":");
        const field = colon < 0 ? line : line.slice(0, colon);
        const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
        if (field === "data") data.push(value);
        else if (field === "event") event = value;
        else if (field === "id") id = value;
      }
      if (data.length) parsed.push({ id, event, data: data.join("\n") });
      boundary = this.buffer.indexOf("\n\n");
    }
    return parsed;
  }
};
function parseJobFrame(message) {
  var _a;
  let payload;
  try {
    payload = JSON.parse(message.data);
  } catch (e) {
    return null;
  }
  if (!payload || typeof payload !== "object") return null;
  const type = typeof payload.type === "string" ? payload.type : message.event;
  if (!JOB_FRAME_TYPES.has(type)) return null;
  if (type === "event" && !Number.isInteger((_a = payload.event) == null ? void 0 : _a.seq)) return null;
  return { ...payload, type };
}
var AimdocJobsClient = class {
  constructor(transport) {
    this.transport = transport;
  }
  /** The identified customer's jobs (their own and their workspace's). Anonymous sessions get []. */
  async list() {
    const payload = await this.transport.request("GET", "/sdk/v1/jobs");
    return payload.data;
  }
  get(jobId) {
    return this.transport.request("GET", `/sdk/v1/jobs/${encodeURIComponent(jobId)}`);
  }
  /**
   * Follow a job live: `status` on connect, `plan` while a wake works,
   * `event` rows as the job logs them, `wake` transitions, `state` updates,
   * `question`s and `approval`s. The stream is long-lived (a job is never
   * terminal while on) and reconnects by itself after an interruption,
   * resuming `event`s after the last seq seen; everything else is a snapshot
   * the server re-sends on connect. Returns the unsubscribe function.
   */
  subscribe(jobId, handlers, options = {}) {
    var _a, _b, _c;
    const controller = new AbortController();
    const signal = controller.signal;
    const unsubscribe = () => controller.abort();
    if (options.signal) {
      if (options.signal.aborted) unsubscribe();
      else options.signal.addEventListener("abort", unsubscribe, { once: true });
    }
    const baseDelay = Math.max(100, (_a = options.reconnectDelayMs) != null ? _a : 1e3);
    const maxDelay = Math.max(baseDelay, (_b = options.maxReconnectDelayMs) != null ? _b : 3e4);
    let cursor = Math.max(0, Math.floor((_c = options.after) != null ? _c : 0));
    let connections = 0;
    let failures = 0;
    const dispatch = (frame) => {
      var _a2, _b2, _c2, _d, _e, _f, _g, _h;
      (_a2 = handlers.onFrame) == null ? void 0 : _a2.call(handlers, frame);
      switch (frame.type) {
        case "event":
          (_b2 = handlers.onEvent) == null ? void 0 : _b2.call(handlers, frame.event, frame);
          break;
        case "status":
          (_c2 = handlers.onStatus) == null ? void 0 : _c2.call(handlers, frame);
          break;
        case "plan":
          (_d = handlers.onPlan) == null ? void 0 : _d.call(handlers, frame.plan, frame);
          break;
        case "wake":
          (_e = handlers.onWake) == null ? void 0 : _e.call(handlers, frame);
          break;
        case "state":
          (_f = handlers.onState) == null ? void 0 : _f.call(handlers, frame);
          break;
        case "question":
          (_g = handlers.onQuestion) == null ? void 0 : _g.call(handlers, frame.request, frame);
          break;
        case "approval":
          (_h = handlers.onApproval) == null ? void 0 : _h.call(handlers, frame.approval, frame);
          break;
      }
    };
    const report = (error, willRetry) => {
      if (signal.aborted || !handlers.onError) return;
      const typed = error instanceof AimdocServicesError ? error : new AimdocServicesError("stream_error", (error == null ? void 0 : error.message) || "Job stream interrupted.");
      handlers.onError(typed, { willRetry });
    };
    const consume = async (response) => {
      var _a2;
      if (!response.body) throw new AimdocServicesError("stream_unavailable", "Job stream has no body.");
      const contentType = response.headers.get("Content-Type") || "";
      if (!contentType.toLowerCase().includes("text/event-stream")) {
        throw new AimdocServicesError("stream_unavailable", "Job stream endpoint did not return text/event-stream.");
      }
      connections += 1;
      failures = 0;
      (_a2 = handlers.onOpen) == null ? void 0 : _a2.call(handlers, { reconnect: connections > 1 });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = new AimdocSseParser();
      try {
        while (!signal.aborted) {
          const { done, value } = await reader.read();
          if (done) return;
          for (const message of parser.push(decoder.decode(value, { stream: true }))) {
            const frame = parseJobFrame(message);
            if (!frame || signal.aborted) continue;
            if (frame.type === "event") {
              if (frame.event.seq <= cursor) continue;
              cursor = frame.event.seq;
            }
            dispatch(frame);
          }
        }
      } finally {
        reader.releaseLock();
      }
    };
    void (async () => {
      while (!signal.aborted) {
        try {
          const response = await this.transport.openStream(
            `/sdk/v1/jobs/${encodeURIComponent(jobId)}/events?after=${cursor}`,
            signal
          );
          await consume(response);
          if (signal.aborted) return;
        } catch (error) {
          if (signal.aborted) return;
          failures += 1;
          const status = error instanceof AimdocServicesError ? error.status : 0;
          const permanent = status === 400 || status === 403 || status === 404;
          report(error, !permanent);
          if (permanent) return;
        }
        const delay = Math.min(maxDelay, baseDelay * 2 ** Math.max(0, failures - 1));
        if (!await waitFor(delay, signal)) return;
      }
    })();
    return unsubscribe;
  }
  /** Tell the job something. Wakes it (when on) and the reply posts back as a job message. */
  steer(jobId, text, options = {}) {
    return this.transport.request("POST", `/sdk/v1/jobs/${encodeURIComponent(jobId)}/steer`, {
      body: {
        text,
        ...options.kind ? { kind: options.kind } : {},
        ...options.conversationId ? { conversation_id: options.conversationId } : {}
      }
    });
  }
  /** Answer one of the job's `open_questions`. The job resumes with the answer. */
  answer(jobId, requestId, payload = {}) {
    return this.transport.request("POST", `/sdk/v1/jobs/${encodeURIComponent(jobId)}/answer`, {
      body: {
        request_id: requestId,
        ...payload.answers !== void 0 ? { answers: payload.answers } : {},
        ...payload.text !== void 0 ? { text: payload.text } : {}
      }
    });
  }
  /** Decide one of the job's `pending_approvals`. */
  approve(jobId, approvalId, approve, note) {
    return this.transport.request("POST", `/sdk/v1/jobs/${encodeURIComponent(jobId)}/approve`, {
      body: { approval_id: approvalId, approve, ...note ? { note } : {} }
    });
  }
  /** One artifact with its body. */
  getArtifact(jobId, artifactId) {
    return this.transport.request(
      "GET",
      `/sdk/v1/jobs/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(artifactId)}`
    );
  }
};
var AimdocServicesClient = class {
  constructor(options) {
    this.staticAssertionUsed = false;
    this.jobs = new AimdocJobsClient(this);
    if (options) this.configure(options);
  }
  configure(options) {
    var _a, _b;
    const publishableKey = (_a = options.publishableKey) == null ? void 0 : _a.trim();
    if (!publishableKey && !options.exchangeSession) {
      throw new AimdocServicesError(
        "publishable_key_required",
        "A Services publishable key (or a custom exchangeSession) is required."
      );
    }
    this.options = {
      publishableKey,
      apiUrl: (options.apiUrl || DEFAULT_SERVICES_API_URL).replace(/\/$/, ""),
      timeoutMs: (_b = options.timeoutMs) != null ? _b : DEFAULT_SERVICES_TIMEOUT_MS,
      identityAssertion: options.identityAssertion,
      getIdentityAssertion: options.getIdentityAssertion,
      exchangeSession: options.exchangeSession
    };
    this.session = void 0;
    this.sessionPromise = void 0;
    this.staticAssertionUsed = false;
    return this;
  }
  /** Replace identity and force the next request to exchange a new session. */
  setIdentityAssertion(assertion) {
    const options = this.requireOptions();
    options.identityAssertion = assertion;
    this.staticAssertionUsed = false;
    this.session = void 0;
    this.sessionPromise = void 0;
  }
  /** Drop authenticated identity; the next request creates an anonymous session. */
  clearIdentity() {
    const options = this.requireOptions();
    options.identityAssertion = void 0;
    options.getIdentityAssertion = void 0;
    this.staticAssertionUsed = false;
    this.session = void 0;
    this.sessionPromise = void 0;
  }
  get identity() {
    var _a;
    return (_a = this.session) == null ? void 0 : _a.identity;
  }
  /**
   * What this customer can buy, request or has: every published service with
   * their job for it (if any) and whether they can start or buy one now.
   */
  async list() {
    const payload = await this.request("GET", "/sdk/v1/services");
    return payload.data;
  }
  /** Start (or restart) the customer's job for an included service. Returns the job. */
  start(key) {
    return this.request("POST", `/sdk/v1/services/${encodeURIComponent(key)}/start`);
  }
  /**
   * Buy a paid service: a Stripe-hosted Checkout session on the vendor's
   * account. Navigate the top window to `url`; the job is created by the
   * webhook once payment lands (re-read `jobs.list()` on return).
   */
  checkout(key, options = {}) {
    const returnUrl = options.returnUrl || (typeof window !== "undefined" ? window.location.href.split("#")[0] : "");
    return this.request("POST", `/sdk/v1/services/${encodeURIComponent(key)}/checkout`, {
      body: { return_url: returnUrl },
      idempotencyKey: options.idempotencyKey || mutationId()
    });
  }
  /**
   * The identified user's customer-facing objectives (see AimdocObjective).
   * Anonymous sessions receive an empty list.
   */
  async listObjectives(options = {}) {
    const query = options.agentId ? `?agent_id=${encodeURIComponent(options.agentId)}` : "";
    const payload = await this.request(
      "GET",
      `/sdk/v1/objectives${query}`
    );
    return payload.data;
  }
  requireOptions() {
    if (!this.options) {
      throw new AimdocServicesError(
        "services_not_configured",
        "Configure Aimdoc Services with a publishable key first."
      );
    }
    return this.options;
  }
  sessionIsFresh() {
    if (!this.session) return false;
    const expiresAt = Date.parse(this.session.expires_at);
    return Number.isFinite(expiresAt) && expiresAt - SESSION_EXPIRY_HEADROOM_MS > Date.now();
  }
  async identityAssertion() {
    var _a;
    const options = this.requireOptions();
    if (options.getIdentityAssertion) {
      return ((_a = await options.getIdentityAssertion()) == null ? void 0 : _a.trim()) || void 0;
    }
    if (options.identityAssertion) {
      if (this.staticAssertionUsed) {
        throw new AimdocServicesError(
          "identity_assertion_refresh_required",
          "The one-time identity assertion was already used. Provide getIdentityAssertion to refresh long-lived SDK sessions."
        );
      }
      return options.identityAssertion.trim();
    }
    return void 0;
  }
  async getSession(force = false) {
    if (!force && this.sessionIsFresh()) return this.session;
    if (this.sessionPromise) return this.sessionPromise;
    const options = this.requireOptions();
    this.sessionPromise = (async () => {
      if (options.exchangeSession) {
        const exchanged = await options.exchangeSession();
        if (!(exchanged == null ? void 0 : exchanged.access_token)) {
          throw new AimdocServicesError(
            "session_exchange_failed",
            "The custom session exchange returned no access token."
          );
        }
        this.session = exchanged;
        return exchanged;
      }
      const assertion = await this.identityAssertion();
      const response = await this.rawFetch("/sdk/v1/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          publishable_key: options.publishableKey,
          ...assertion ? { identity_assertion: assertion } : {}
        })
      });
      if (assertion && !options.getIdentityAssertion && options.identityAssertion) {
        this.staticAssertionUsed = true;
      }
      const session = await this.parseResponse(response);
      this.session = session;
      return session;
    })();
    try {
      return await this.sessionPromise;
    } finally {
      this.sessionPromise = void 0;
    }
  }
  /** @internal Authenticated JSON request; retries once on 401 with a fresh session. */
  async request(method, path, options = {}, retrySession = true) {
    const session = await this.getSession();
    const headers = {
      Authorization: `Bearer ${session.access_token}`,
      "X-Aimdoc-SDK-Version": SDK_VERSION
    };
    if (options.body !== void 0) headers["Content-Type"] = "application/json";
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    const response = await this.rawFetch(path, {
      method,
      headers,
      body: options.body === void 0 ? void 0 : JSON.stringify(options.body)
    });
    if (response.status === 401 && retrySession) {
      this.session = void 0;
      return this.request(method, path, options, false);
    }
    return await this.parseResponse(response);
  }
  /** @internal Open an authenticated SSE response (no timeout; the caller's signal ends it). */
  async openStream(path, signal, retrySession = true) {
    const options = this.requireOptions();
    const session = await this.getSession();
    if (typeof globalThis.fetch !== "function") {
      throw new AimdocServicesError(
        "fetch_unavailable",
        "Aimdoc Services requires a runtime with fetch support."
      );
    }
    let response;
    try {
      response = await globalThis.fetch(`${options.apiUrl}${path}`, {
        method: "GET",
        credentials: "omit",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${session.access_token}`,
          "X-Aimdoc-SDK-Version": SDK_VERSION
        },
        signal
      });
    } catch (error) {
      if ((error == null ? void 0 : error.name) === "AbortError") throw error;
      throw new AimdocServicesError(
        "network_error",
        (error == null ? void 0 : error.message) || "Aimdoc Services stream request failed."
      );
    }
    if (response.status === 401 && retrySession) {
      this.session = void 0;
      return this.openStream(path, signal, false);
    }
    if (!response.ok) await this.parseResponse(response);
    return response;
  }
  async rawFetch(path, init) {
    const options = this.requireOptions();
    if (typeof globalThis.fetch !== "function") {
      throw new AimdocServicesError(
        "fetch_unavailable",
        "Aimdoc Services requires a runtime with fetch support."
      );
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs);
    try {
      return await globalThis.fetch(`${options.apiUrl}${path}`, {
        ...init,
        credentials: "omit",
        signal: controller.signal
      });
    } catch (error) {
      if ((error == null ? void 0 : error.name) === "AbortError") {
        throw new AimdocServicesError("request_timeout", "Aimdoc Services request timed out.");
      }
      throw new AimdocServicesError(
        "network_error",
        (error == null ? void 0 : error.message) || "Aimdoc Services request failed."
      );
    } finally {
      clearTimeout(timeout);
    }
  }
  async parseResponse(response) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (e) {
    }
    if (response.ok) return payload;
    const detail = payload == null ? void 0 : payload.detail;
    const code = detail && typeof detail === "object" && detail.code || (payload == null ? void 0 : payload.code) || `http_${response.status}`;
    const message = detail && typeof detail === "object" && detail.message || typeof detail === "string" && detail || (payload == null ? void 0 : payload.message) || `Aimdoc Services request failed (${response.status}).`;
    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfter = retryAfterHeader === null ? NaN : Number(retryAfterHeader);
    throw new AimdocServicesError(String(code), String(message), {
      status: response.status,
      retryAfter: Number.isFinite(retryAfter) ? retryAfter : void 0
    });
  }
};
var CLIENT_TOOLS_GLOBAL = "__aimdocClientTools";
var CLIENT_TOOLS_CHANGE_EVENT = "aimdoc:toolchange";
var TOOL_NAME_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
var clientToolRegistry = () => {
  const host = window;
  if (!(host[CLIENT_TOOLS_GLOBAL] instanceof Map)) {
    host[CLIENT_TOOLS_GLOBAL] = /* @__PURE__ */ new Map();
  }
  return host[CLIENT_TOOLS_GLOBAL];
};
var announceToolChange = () => {
  window.dispatchEvent(new CustomEvent(CLIENT_TOOLS_CHANGE_EVENT));
};
var Aimdoc = class _Aimdoc {
  constructor() {
    this.identify = (user) => {
      this.send({ type: "aimdoc:identify", user });
    };
    this.openChat = (options) => {
      this.send({ type: "aimdoc:openChat", ...options });
    };
    /** Close the chat if it's open. */
    this.closeChat = (options) => {
      this.send({ type: "aimdoc:close", ...options });
    };
    /** Open the chat if it's closed, close it if it's open. */
    this.toggleChat = (options) => {
      this.send({ type: "aimdoc:toggle", ...options });
    };
    this.sendMessage = (message, options) => {
      this.send({ type: "aimdoc:sendMessage", message, ...options });
    };
    /**
     * Open the embedded agent about one objective: the agent explains what
     * the user needs to do to complete it (and can mark progress as they go).
     * The objective key rides along as developer context on that message so
     * the agent knows exactly which objective is meant.
     */
    this.askAboutObjective = (objective, options) => {
      var _a;
      const target = (options == null ? void 0 : options.agentId) ? { agentId: options.agentId } : void 0;
      this.setNextMessageContext({ objective_key: objective.key, objective_name: objective.name }, target);
      this.sendMessage(
        (_a = options == null ? void 0 : options.message) != null ? _a : `I'd like to complete this step: "${objective.name}". What do I need to do?`,
        target
      );
    };
    this.setNextMessageContext = (context, options) => {
      this.send({ type: "aimdoc:setNextMessageContext", context, ...options });
    };
    this.setDisplayOptions = (options, target) => {
      this.send({ type: "aimdoc:setDisplayOptions", options, ...target });
    };
    this.configureServices = (options) => this.services.configure(options);
    /**
     * Register a tool the agent can call in the user's browser. Register it
     * while the capability exists on the page (for example from the component
     * that owns it) and unregister when it goes away; the agent is only offered
     * what is registered right now.
     *
     * Returns a function that unregisters the tool. Throws on an invalid
     * descriptor or a name that is already registered. Browser tools must be
     * enabled for the agent in Aimdoc (agent builder → MCP Servers).
     */
    this.registerTool = (tool, options) => {
      var _a, _b;
      if (!tool || typeof tool !== "object") {
        throw new TypeError("aimdoc.registerTool: a tool descriptor is required.");
      }
      if (typeof tool.name !== "string" || !TOOL_NAME_PATTERN.test(tool.name)) {
        throw new TypeError(
          'aimdoc.registerTool: name must be 1-64 characters of letters, digits, "_" or "-".'
        );
      }
      if (typeof tool.description !== "string" || !tool.description.trim()) {
        throw new TypeError(`aimdoc.registerTool: "${tool.name}" needs a description.`);
      }
      if (typeof tool.execute !== "function") {
        throw new TypeError(`aimdoc.registerTool: "${tool.name}" needs an execute function.`);
      }
      if (typeof window === "undefined" || ((_a = options == null ? void 0 : options.signal) == null ? void 0 : _a.aborted)) {
        return () => {
        };
      }
      const registry = clientToolRegistry();
      if (registry.has(tool.name)) {
        throw new Error(`aimdoc.registerTool: a tool named "${tool.name}" is already registered.`);
      }
      const registered = (options == null ? void 0 : options.agentId) ? { ...tool, agentId: options.agentId } : { ...tool };
      registry.set(tool.name, registered);
      announceToolChange();
      const unregister = () => {
        if (registry.get(tool.name) === registered) {
          registry.delete(tool.name);
          announceToolChange();
        }
      };
      (_b = options == null ? void 0 : options.signal) == null ? void 0 : _b.addEventListener("abort", unregister, { once: true });
      return unregister;
    };
    /** The tools currently registered on this page, without their `execute` functions. */
    this.getTools = () => {
      if (typeof window === "undefined") return [];
      const definitions = [];
      clientToolRegistry().forEach((tool) => {
        const { execute: _execute, agentId: _agentId, ...definition } = tool;
        definitions.push(definition);
      });
      return definitions;
    };
    this.send = (data) => {
      if (typeof window === "undefined") return;
      window.postMessage({ ...data, sdkVersion: SDK_VERSION }, "*");
    };
    this.services = new AimdocServicesClient();
  }
  /** The customer's jobs (`list`, `get`, `subscribe`, `steer`, `answer`, `approve`). Shares `services`' session. */
  get jobs() {
    return this.services.jobs;
  }
  static getInstance() {
    if (!_Aimdoc.instance) {
      _Aimdoc.instance = new _Aimdoc();
    }
    return _Aimdoc.instance;
  }
};
var aimdoc = Aimdoc.getInstance();
export {
  Aimdoc,
  AimdocJobsClient,
  AimdocServicesClient,
  AimdocServicesError,
  AimdocSseParser,
  SDK_VERSION,
  aimdoc,
  parseJobFrame
};
